import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Mail, Phone, MapPin, Trash2, ExternalLink } from "lucide-react";

const STATUS = ["nuevo", "contactado", "convertido", "descartado"];
const STATUS_STYLE = {
  nuevo: "bg-[#EAF0FF] text-[#2C49A0]",
  contactado: "bg-[#FFF7DA] text-[#9C7A18]",
  convertido: "bg-[#E9F8EF] text-[#16A34A]",
  descartado: "bg-[#FDECEC] text-[#DC2626]",
};

export default function AdminMarketing() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/marketing/leads");
      setLeads(data.leads || []);
      setStats(data.stats || {});
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (lead, status) => {
    try {
      await api.patch(`/marketing/leads/${lead.id}`, { status, notes: lead.notes || "" });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status } : l)));
      toast.success("Estado actualizado.");
      load();
    } catch { toast.error("No se pudo actualizar."); }
  };

  const remove = async (id) => {
    try { await api.delete(`/marketing/leads/${id}`); setLeads((p) => p.filter((l) => l.id !== id)); load(); }
    catch { toast.error("No se pudo eliminar."); }
  };

  const publicUrl = `${window.location.origin}/contacto`;

  return (
    <div className="space-y-6" data-testid="admin-marketing-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#A43790]">Marketing y Captación</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547] flex items-center gap-2">
            <Megaphone className="text-[#A43790]" /> Prospectos (CRM)
          </h1>
          <p className="text-sm text-[#6E7591] mt-1">Gestiona los leads capturados desde tu formulario público.</p>
        </div>
        <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="public-form-link"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border-2 border-[#E1E5F0] text-[#1F2547] font-bold text-sm hover:border-[#A43790]">
          <ExternalLink size={16} /> Ver formulario público
        </a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="marketing-stats">
        {STATUS.map((s) => (
          <div key={s} className="jasq-card p-4">
            <p className="text-2xl font-extrabold text-[#1F2547]">{stats[s] || 0}</p>
            <p className={`text-xs font-bold capitalize inline-block px-2 py-0.5 rounded-full mt-1 ${STATUS_STYLE[s]}`}>{s}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="jasq-card p-10 text-center text-sm text-[#6E7591]">Cargando…</div>
      ) : leads.length === 0 ? (
        <div className="jasq-card p-10 text-center text-sm text-[#6E7591]" data-testid="marketing-empty">
          Aún no hay prospectos. Comparte tu formulario público: <b>{publicUrl}</b>
        </div>
      ) : (
        <div className="jasq-card overflow-x-auto" data-testid="leads-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#6E7591] border-b border-[#E1E5F0]">
                <th className="p-3">Prospecto</th><th className="p-3">Contacto</th><th className="p-3">Interés</th><th className="p-3">Estado</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-[#F2F4FA]" data-testid={`lead-${l.id}`}>
                  <td className="p-3">
                    <p className="font-bold text-[#1F2547]">{l.name}</p>
                    {l.city && <p className="text-xs text-[#A6ACC1] flex items-center gap-1"><MapPin size={11} />{l.city}</p>}
                  </td>
                  <td className="p-3">
                    <p className="flex items-center gap-1 text-[#4C5478]"><Mail size={12} />{l.email}</p>
                    {l.phone && <p className="flex items-center gap-1 text-[#4C5478]"><Phone size={12} />{l.phone}</p>}
                  </td>
                  <td className="p-3 text-[#4C5478] capitalize">{l.interest || "—"}</td>
                  <td className="p-3">
                    <select value={l.status} onChange={(e) => setStatus(l, e.target.value)} data-testid={`lead-status-${l.id}`}
                      className={`text-xs font-bold rounded-full px-2 py-1 border-0 outline-none cursor-pointer ${STATUS_STYLE[l.status] || ""}`}>
                      {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => remove(l.id)} data-testid={`delete-lead-${l.id}`} className="text-[#D63384] hover:bg-[#FDEBF4] rounded-lg p-1.5"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
